import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { SIGNATURE_HEIGHT, SIGNATURE_WIDTH } from './signature-pad.constants';
import { SignaturePadCanvas } from './signature-pad.style';

export interface SignaturePadActions {
  transformToSVG: () => string;
  clear: VoidFunction;
}

interface Point {
  x: number;
  y: number;
}

interface SignaturePadProps {
  onChange?: () => void;
}

export const SignaturePad = forwardRef<SignaturePadActions, SignaturePadProps>((props, ref) => {
  const { onChange } = props;

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [paths, setPaths] = useState<Point[][]>([]);
  const [currentPath, setCurrentPath] = useState<Point[]>([]);
  const [drawing, setDrawing] = useState(false);

  useImperativeHandle(ref, () => ({
    transformToSVG: generateSVGBase64,
    clear: clearCanvas,
  }));

  useEffect(() => {
    if (paths.length > 0) {
      onChange();
    }
  }, [paths, onChange]);

  // Redibuja todos los paths en el canvas
  const redraw = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      ctx.clearRect(0, 0, SIGNATURE_WIDTH, SIGNATURE_HEIGHT);
      ctx.beginPath();

      for (const path of paths) {
        if (path.length > 0) {
          ctx.moveTo(path[0].x, path[0].y);
          for (let i = 1; i < path.length; i++) {
            ctx.lineTo(path[i].x, path[i].y);
          }
        }
      }

      if (currentPath.length > 0) {
        ctx.moveTo(currentPath[0].x, currentPath[0].y);
        for (let i = 1; i < currentPath.length; i++) {
          ctx.lineTo(currentPath[i].x, currentPath[i].y);
        }
      }

      ctx.stroke();
    },
    [paths, currentPath]
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }

    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.lineWidth = 2;
    ctx.strokeStyle = 'black';
    redraw(ctx);
  }, [paths, redraw]);

  // The rendered size differs from the internal resolution, so the pointer is scaled to it.
  const getPointerPosition = (
    e: React.MouseEvent | React.TouchEvent,
    canvas: HTMLCanvasElement
  ) => {
    const rect = canvas.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    const scaleX = rect.width ? SIGNATURE_WIDTH / rect.width : 1;
    const scaleY = rect.height ? SIGNATURE_HEIGHT / rect.height : 1;

    return { x: (clientX - rect.left) * scaleX, y: (clientY - rect.top) * scaleY };
  };

  // Eventos de dibujo
  const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const pos = getPointerPosition(e, canvas);
    setCurrentPath([{ x: pos.x, y: pos.y }]);
    setDrawing(true);
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    if (!drawing) {
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const pos = getPointerPosition(e, canvas);
    setCurrentPath(prev => [...prev, { x: pos.x, y: pos.y }]);
  };

  const stopDrawing = () => {
    if (!drawing) {
      return;
    }
    setDrawing(false);
    setPaths(prev => [...prev, currentPath]);
    setCurrentPath([]);
  };

  const generateSVG = () => {
    if (!paths.length) {
      return null;
    }

    const svgPaths = paths
      .map(path => {
        const d = path
          .map((p, i) =>
            i === 0
              ? `M ${p.x.toFixed(1)} ${p.y.toFixed(1)}`
              : `L ${p.x.toFixed(1)} ${p.y.toFixed(1)}`
          )
          .join(' ');
        return `<path d="${d}" stroke="black" stroke-width="2" fill="none" />`;
      })
      .join('');

    return `<svg xmlns="http://www.w3.org/2000/svg" width="${SIGNATURE_WIDTH}" height="${SIGNATURE_HEIGHT}">
          ${svgPaths}
        </svg>`;
  };

  const generateSVGBase64 = (): string => {
    const svg = generateSVG();
    if (!svg) {
      return '';
    }
    const encoded = btoa(svg);
    return `data:image/svg+xml;base64,${encoded}`;
  };

  const clearCanvas = () => {
    setPaths([]);
    setCurrentPath([]);
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx?.clearRect(0, 0, canvas.width, canvas.height);
    }
  };

  return (
    <SignaturePadCanvas
      ref={canvasRef}
      width={SIGNATURE_WIDTH}
      height={SIGNATURE_HEIGHT}
      data-testid="signature-pad"
      className={paths.length > 0 ? 'filled' : 'empty'}
      onMouseDown={startDrawing}
      onMouseMove={draw}
      onMouseUp={stopDrawing}
      onMouseLeave={stopDrawing}
      onTouchStart={startDrawing}
      onTouchMove={draw}
      onTouchEnd={stopDrawing}
    />
  );
});
